import { useEffect, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ExternalLink, Radio, Plus, Minus, Pencil } from "lucide-react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { getMapboxToken } from "@/lib/mapbox";
import { addZonePolygonLayers, removeZonePolygonLayers, resolveZoneLabel } from "@/lib/zoneStyle";
import { parsePolygon } from "@/types/domain";
import { dedupeLatestLocationByUser } from "@/lib/dedupeLatestLocationByUser";
import { useSidebar } from "@/contexts/SidebarContext";
import {
  festivalDayIso,
  getInitials,
  markerOpacityForAge,
  CREW_MARKER_SIZE,
  hasLiveMapAccess,
} from "@produce/shared";
import { useProductionTierAccess } from "@/hooks/useProductionTierAccess";
import { useCrewAvatars } from "@/hooks/useCrewAvatars";
import { createCrewMarkerElement, updateCrewMarkerElement } from "@/lib/crewMarkerElement";
import { useCrewShiftProgress } from "@/hooks/useCrewShiftProgress";

interface LiveTrackingPreviewCardProps {
  productionId: string;
  navigationAddress?: string | null;
}

const INITIAL_ZOOM = 16;
const MAX_BUILD_ATTEMPTS = 3;
const BUILD_RETRY_MS = 2000;

export const LiveTrackingPreviewCard = ({
  productionId,
  navigationAddress: _navigationAddress,
}: LiveTrackingPreviewCardProps) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const isMounted = useRef(true);
  const isInitializing = useRef(false);
  const drawnAreaIds = useRef<string[]>([]);
  const [mapLoaded, setMapLoaded] = useState(false);
  // Bumped to retry a build that failed. Before the map was built once per
  // data change, a failed token fetch was silently retried by the next
  // re-render; building exactly once removed that safety net, so the card
  // asks for the retry explicitly instead.
  const [buildAttempt, setBuildAttempt] = useState(0);
  const { expanded } = useSidebar();
  const { urlFor: avatarUrlFor, avatars: crewAvatars } = useCrewAvatars(productionId);
  // Lock B: live crew positions are PRO/STUDIO. This card used to poll
  // crew_locations with NO tier check at all — the one surface that leaked
  // positions to any producer. The RLS (migration 20260828100000) now
  // hides the rows server-side; this gate stops the pointless polling and
  // keeps the preview honest. The mini-map itself (zones, basemap) is
  // Lock A content and stays.
  const { hasAccess: hasLiveCrew } = useProductionTierAccess(productionId, hasLiveMapAccess);

  // Resize map when sidebar changes
  useEffect(() => {
    if (map.current) {
      setTimeout(() => {
        map.current?.resize();
      }, 200);
    }
  }, [expanded]);

  // Determine selected day (today or first festival day)
  const { data: briefing } = useQuery({
    queryKey: ["briefing-preview", productionId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("briefings")
        .select("festival_date_start, festival_date_end, timezone")
        .eq("id", productionId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!productionId,
  });

  // Festival day in the briefing's own timezone once it has loaded; the
  // viewer clock (minus the 06:00 rollover) until then.
  const today = festivalDayIso(new Date(), briefing?.timezone || undefined);
  const selectedDay = briefing?.festival_date_start
    ? today >= briefing.festival_date_start.split("T")[0] &&
      today <=
        (briefing.festival_date_end?.split("T")[0] || briefing.festival_date_start.split("T")[0])
      ? today
      : briefing.festival_date_start.split("T")[0]
    : today;

  // Fetch crew locations
  const { data: crewLocations } = useQuery({
    queryKey: ["crew-locations-preview", productionId, selectedDay],
    queryFn: async () => {
      // Get latest location per user
      const { data: locations, error: locError } = await supabase
        .from("crew_locations")
        .select("id, user_id, briefing_id, latitude, longitude, timestamp")
        .eq("briefing_id", productionId)
        // Honour the per-crew "share with producer" opt-out. Previously
        // the setting wrote visible_to_producer on the row but no reader
        // actually filtered on it, so the toggle had no effect.
        .eq("visible_to_producer", true)
        .order("timestamp", { ascending: false });

      if (locError) throw locError;
      if (!locations || locations.length === 0) return [];

      // Deduplicate to the latest fix per user (reads raw crew_locations,
      // which has many rows per user). Shared with LiveTracking.tsx.
      const uniqueLocations = dedupeLatestLocationByUser(locations);
      const userIds = uniqueLocations.map((loc) => loc.user_id);

      // Fetch crew member details
      const { data: crewMembers, error: crewError } = await supabase
        .from("crew_members")
        .select("id, user_id, name, color")
        .eq("briefing_id", productionId)
        .eq("event_date", selectedDay)
        .in("user_id", userIds);

      if (crewError) throw crewError;

      const crewMemberMap = new Map(crewMembers?.map((cm) => [cm.user_id, cm]) || []);

      return uniqueLocations
        .filter((loc) => crewMemberMap.has(loc.user_id))
        .map((loc) => ({
          ...loc,
          crew_member: crewMemberMap.get(loc.user_id),
        }));
    },
    enabled: hasLiveCrew && !!productionId && !!selectedDay,
    refetchInterval: 30000,
  });

  // Progress rings from the same source as the full live-tracking map.
  const { shiftStateByCrewMember } = useCrewShiftProgress(
    productionId,
    selectedDay,
    briefing?.timezone || undefined,
  );

  // Fetch festival areas
  const { data: festivalAreas } = useQuery({
    queryKey: ["festival-areas-preview", productionId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("festival_areas")
        .select("*, briefing_stages(label)")
        .eq("briefing_id", productionId)
        .eq("area_type", "stage_zone");

      if (error) throw error;
      return data;
    },
    enabled: !!productionId,
  });

  // Initialize map with robust race condition handling
  useEffect(() => {
    isMounted.current = true;

    const initMap = async () => {
      // Guard: prevent double initialization
      if (isInitializing.current || map.current) {
        return;
      }

      if (!mapContainer.current) {
        return;
      }

      isInitializing.current = true;

      try {
        const token = await getMapboxToken();

        // Check if still mounted after async operation
        if (!isMounted.current) {
          isInitializing.current = false;
          return;
        }

        mapboxgl.accessToken = token;

        // Ensure container still exists and is empty
        if (!mapContainer.current) {
          isInitializing.current = false;
          return;
        }

        // Synchronously clean the container before creating map
        mapContainer.current.innerHTML = "";

        let center: [number, number] = [4.9041, 52.3676]; // Default to Amsterdam

        // Try to get producer's current location
        if (navigator.geolocation) {
          try {
            const position = await new Promise<GeolocationPosition>((resolve, reject) => {
              navigator.geolocation.getCurrentPosition(resolve, reject, {
                enableHighAccuracy: true,
                timeout: 5000,
                maximumAge: 60000,
              });
            });

            // Check again after geolocation
            if (!isMounted.current) {
              isInitializing.current = false;
              return;
            }

            center = [position.coords.longitude, position.coords.latitude];
          } catch {
            // Could not get producer location, using default
          }
        }

        // Final check before creating map
        if (!isMounted.current || !mapContainer.current) {
          isInitializing.current = false;
          return;
        }

        map.current = new mapboxgl.Map({
          container: mapContainer.current,
          style: "mapbox://styles/mapbox/satellite-streets-v12",
          center,
          zoom: INITIAL_ZOOM,
          interactive: true,
        });

        // Disable scroll zoom but keep click/drag
        map.current.scrollZoom.disable();

        map.current.on("load", () => {
          if (!isMounted.current) return;
          setMapLoaded(true);
        });

        isInitializing.current = false;
      } catch (error) {
        console.error(error);
        isInitializing.current = false;
        // getMapboxToken only caches successes, so a retry can succeed where
        // the first attempt failed (5xx, dropped connection, a session that
        // needed refreshing on tab wake).
        if (isMounted.current && buildAttempt < MAX_BUILD_ATTEMPTS) {
          retryTimer = setTimeout(() => setBuildAttempt((n) => n + 1), BUILD_RETRY_MS);
        }
      }
    };

    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    initMap();

    return () => {
      isMounted.current = false;
      isInitializing.current = false;
      setMapLoaded(false);
      clearTimeout(retryTimer);

      if (map.current) {
        map.current.remove();
        map.current = null;
      }
      drawnAreaIds.current = [];
    };
    // Build the map once — plus once more per retry after a failed build.
    // Rebuilding it whenever the zones arrived or a zoom button was pressed is
    // what made this card visibly blink: the old map was torn down and a fresh
    // one streamed its satellite tiles again.
  }, [buildAttempt]);

  // Draw the stage zones on the existing map, and redraw when they change.
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    for (const areaId of drawnAreaIds.current) {
      removeZonePolygonLayers(map.current, areaId);
    }
    drawnAreaIds.current = [];

    festivalAreas?.forEach((area) => {
      if (!map.current) return;

      const parsed = parsePolygon(area.polygon);
      if (!parsed) return;

      // Resolve the stage label through the FK join (canonical), with
      // the same fallbacks as the full live-tracking map (shared helper).
      const label = resolveZoneLabel({
        areaType: area.area_type,
        fkLabel: (area as { briefing_stages?: { label?: string } | null }).briefing_stages?.label,
        name: area.name,
        stageId: area.stage_id,
      });

      // Fill, border and rounded pill label all use the shared zone
      // look (single source of truth in lib/zoneStyle) so this preview
      // card can never drift from the full live-tracking map.
      addZonePolygonLayers(map.current, {
        areaId: area.id,
        areaType: area.area_type,
        coordinates: parsed.coordinates,
        label,
      });
      drawnAreaIds.current.push(area.id);
    });
  }, [festivalAreas, mapLoaded]);

  // Update markers when crew locations change
  useEffect(() => {
    if (!map.current || !crewLocations) return;

    const markers: mapboxgl.Marker[] = [];

    crewLocations.forEach((location) => {
      // Same marker as the full map, just smaller — the geometry scales
      // from one shared definition (see lib/crewMarkerElement).
      const wrapper = createCrewMarkerElement(CREW_MARKER_SIZE.preview);
      const crewMemberId = location.crew_member?.id;
      const shift = crewMemberId ? shiftStateByCrewMember.get(crewMemberId) : undefined;
      const ageSeconds = (Date.now() - new Date(location.timestamp).getTime()) / 1000;

      updateCrewMarkerElement(wrapper, {
        initials: getInitials(location.crew_member?.name || "??"),
        photoUrl: avatarUrlFor(location.user_id),
        ownColor: location.crew_member?.color,
        progress: shift?.progress ?? null,
        opacity: markerOpacityForAge(ageSeconds),
        // No pointer at this size — it would be more arrowhead than marker.
        showTail: false,
      });

      const marker = new mapboxgl.Marker({ element: wrapper })
        .setLngLat([location.longitude, location.latitude])
        .addTo(map.current!);

      markers.push(marker);
    });

    return () => {
      markers.forEach((m) => m.remove());
    };
  }, [crewLocations, crewAvatars, shiftStateByCrewMember, avatarUrlFor]);

  // Zoom controls. Nudge the existing map — do NOT drive this through React
  // state: the map used to be rebuilt from scratch on every click.
  const handleZoomIn = () => {
    map.current?.setZoom(Math.min(map.current.getZoom() + 1, 20));
  };

  const handleZoomOut = () => {
    map.current?.setZoom(Math.max(map.current.getZoom() - 1, 10));
  };

  return (
    <Card className="flex h-full flex-col border-border bg-card p-6">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-lg font-semibold">
          <Radio className="h-5 w-5" />
          Live tracking
        </h3>
        <Button variant="outline" size="sm" asChild>
          <Link to={`/briefings/${productionId}/edit?step=7&shortcut=true`}>
            <Pencil className="mr-1.5 h-3.5 w-3.5" />
            Edit
          </Link>
        </Button>
      </div>
      <div className="relative mb-4 flex-1" style={{ minHeight: 200 }}>
        <div
          ref={mapContainer}
          className="absolute inset-0 h-full w-full overflow-hidden rounded-lg"
        />
        {/* Zoom controls */}
        <div className="absolute right-2 top-2 z-10 flex flex-col gap-1">
          <Button
            variant="secondary"
            size="icon"
            className="h-8 w-8 bg-background/90 hover:bg-background"
            onClick={handleZoomIn}
            aria-label="Zoom in"
          >
            <Plus className="h-4 w-4" />
          </Button>
          <Button
            variant="secondary"
            size="icon"
            className="h-8 w-8 bg-background/90 hover:bg-background"
            onClick={handleZoomOut}
            aria-label="Zoom out"
          >
            <Minus className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <Button variant="ghost" className="mt-auto w-full text-primary hover:text-primary" asChild>
        <Link to={`/productions/${productionId}/live-tracking`}>
          View full live tracking
          <ExternalLink className="ml-2 h-4 w-4" />
        </Link>
      </Button>
    </Card>
  );
};
